import { ProductionArrayRow } from "../hooks/useProductionArrayData";
import { ProductionArrayUnits } from "./productionArray";

/**
 * The table's column headers, in the order it draws them.
 *
 * Lives here rather than in the table so the export cannot drift from what is
 * on screen: the unit in each header is the response's own, and a header the
 * export wrote out by hand would keep saying bbl after a metric org's response
 * answered in m3.
 */
export const buildProductionArrayHeaders = (units: ProductionArrayUnits): string[] => [
  "Month",
  "Months",
  `Oil (${units.oil.volume})`,
  `Oil (${units.oil.rate})`,
  `Gas (${units.gas.volume})`,
  `Gas (${units.gas.rate})`,
  `Water (${units.water.volume})`,
  `Water (${units.water.rate})`
];

/** One cell of the export: a label, a number, or nothing at all. */
export type ProductionArrayCell = string | number | null;

/**
 * A number as a spreadsheet should receive it: as a NUMBER, without the
 * thousands separator the cell adds. Excel reads "1,234" as text and will not
 * sum a column of it.
 *
 * Full precision, unlike the rounded cell on screen: a spreadsheet is where the
 * numbers get worked, and a normalized month of 0.3 bbl/d would round to 0.
 * Trimmed to 12 significant digits only to drop float noise from the unit
 * scaling (9906.999999999998 for 9907).
 *
 * Nothing at all for a month the response had no value for. A dash is what the
 * table shows, and a dash in a numeric column is text too.
 */
const cell = (value: Maybe<number>): ProductionArrayCell =>
  value == null || !Number.isFinite(value) ? null : Number(value.toPrecision(12));

/**
 * The month a row belongs to, carrying its year.
 *
 * On screen the year comes from the group row above, and the month cell is
 * "Jan" alone. The export is flat, so a bare month name would repeat once per
 * year of production with nothing to tell the copies apart.
 */
const monthCell = (row: ProductionArrayRow): string =>
  row.calendar ? `${row.calendar.label} ${row.calendar.year}` : "";

/**
 * The table as a grid of cells, header row first.
 *
 * `rows` is the flattened rendered order, so the export is the table read top
 * to bottom - with every year present, whether or not its group is open.
 *
 * Normalized rows lead with a row naming the field: the headers carry only the
 * divisor ("per 1000 ft"), which does not say what was measured in feet.
 */
export const productionArrayGrid = (
  rows: readonly ProductionArrayRow[],
  units: ProductionArrayUnits,
  normalizedBy?: Maybe<string>
): ProductionArrayCell[][] => [
  ...(normalizedBy ? [[`Normalized by: ${normalizedBy}`]] : []),
  buildProductionArrayHeaders(units),
  ...rows.map((row) => [
    monthCell(row),
    row.monthsIndex,
    cell(row.oilVolume),
    cell(row.oilRate),
    cell(row.gasVolume),
    cell(row.gasRate),
    cell(row.waterVolume),
    cell(row.waterRate)
  ])
];

/**
 * Tabs for the clipboard, commas for the file.
 *
 * A comma-separated field carrying a comma, a quote or a newline is wrapped
 * and its quotes doubled, which is the same rule `utils/exportUtils` follows.
 * No unit or month name needs it today; the escape is here so a response that
 * names a unit with a comma in it cannot shift every column after it.
 */
export const toDelimitedText = (
  grid: readonly (readonly ProductionArrayCell[])[],
  delimiter: "\t" | ","
): string =>
  grid
    .map((row) =>
      row
        .map((cellValue) => {
          const text = cellValue == null ? "" : String(cellValue);
          return delimiter === "," && /[",\n]/.test(text)
            ? `"${text.replace(/"/g, '""')}"`
            : text;
        })
        .join(delimiter)
    )
    .join("\n");
